/**
 * Why the sign-in or enlist button is refusing, in words.
 *
 * ── The bug this exists for ─────────────────────────────────────────────────
 *
 * *"The Enlist button is always inactive even when I filled out all info."* And
 * it was — every field correct, and a dead button with **nothing on screen
 * saying why**. The cause was not the form at all: the page had been opened over
 * a LAN address (`http://192.168.10.199:4173`) rather than over `localhost`, and
 * a plain-http LAN origin is not a *secure context*, so the browser withholds
 * `crypto.subtle`. ALTCHA hashes its proof-of-work with Web Crypto, so it can
 * never reach `verified`, so `altchaPayload` stays empty, so the button stays
 * disabled — for ever, and silently. The challenge endpoint answered 200
 * throughout, which is what made it look like a form bug.
 *
 * Measured, same build, same viewport, filled form:
 *
 * | origin | secure context | `crypto.subtle` | altcha | button |
 * |---|---|---|---|---|
 * | `http://localhost:4173` | true | object | verified | enabled |
 * | `http://192.168.10.199:4173` | false | undefined | unverified | disabled |
 *
 * **So the fix is not to loosen the gate.** The gate is right: the server
 * re-verifies the payload and a client that skipped it would be refused anyway.
 * What was missing is the sentence. A control that is disabled for a reason the
 * player cannot see is indistinguishable from a broken one — the same rule that
 * put labels on the orders panel and took the dead number-key row off the HUD.
 *
 * ── Why a module rather than a computed in the component ────────────────────
 *
 * Every branch here is a sentence somebody reads at the one moment they are
 * deciding whether this game works, and each is wrong in a way no screenshot
 * shows: an off-by-one on a minimum, an ordering that reports the transient
 * problem while a terminal one is standing. So it is pure and it is tested,
 * and the component draws what it is handed — the discipline `hud-readout.ts`
 * and `daily-quest-box.ts` already follow.
 */

/** Matches the server's own minimum. */
export const NICKNAME_MIN = 3;
/** Matches the server's own minimum. */
export const PASSWORD_MIN = 8;

export interface JoinGateInput {
  /** Enlisting rather than signing in — the only mode with a call sign. */
  register: boolean;
  nickname: string;
  email: string;
  password: string;
  /** Register only — must equal `password`. Ignored while signing in. */
  repeatPassword: string;
  /** Register only — the terms-and-conditions checkbox. */
  agreedToTerms: boolean;
  /** The proof-of-work has been solved and a payload is in hand. */
  captchaSolved: boolean;
  /**
   * Whether the browser will even lend us the primitive the proof-of-work needs.
   * Passed in rather than read here, so this module never touches an ambient
   * global and stays testable without a DOM.
   */
  captchaCapable: boolean;
  /** A request is already in flight. */
  busy: boolean;
}

export type JoinBlockCode =
  | 'insecure-context'
  | 'nickname-short'
  | 'email-missing'
  | 'password-missing'
  | 'password-short'
  | 'password-mismatch'
  | 'terms-required'
  | 'captcha-pending';

export interface JoinBlock {
  code: JoinBlockCode;
  message: string;
}

/**
 * The one reason worth showing, or `null` when the form is ready.
 *
 * **The order is the design.** `insecure-context` comes first because it is the
 * only terminal one — nothing the player types will clear it, so reporting a
 * short password ahead of it would send them to fix the wrong thing. Field
 * problems come next because they are the ones a player can act on. The captcha
 * is last because it is transient and clears itself: a form completed while the
 * proof-of-work is still running should say *waiting*, not *invalid*.
 *
 * `busy` yields `null` deliberately. The button already reads "Working…", and a
 * second explanation of the same state is noise.
 */
export function joinBlockReason(input: JoinGateInput): JoinBlock | null {
  if (input.busy) return null;

  if (!input.captchaCapable) {
    return {
      code: 'insecure-context',
      message:
        'The security check cannot run on this address. Open the game over HTTPS, ' +
        'or on localhost — a plain http:// address that is not localhost is not a ' +
        'secure context, and the browser withholds the cryptography the check needs.',
    };
  }

  if (input.register && input.nickname.trim().length < NICKNAME_MIN) {
    return {
      code: 'nickname-short',
      message: `Your call sign needs at least ${NICKNAME_MIN} characters.`,
    };
  }
  if (input.email.trim().length === 0) {
    return { code: 'email-missing', message: 'Enter the email address for your account.' };
  }
  if (input.password.length === 0) {
    return { code: 'password-missing', message: 'Enter your password.' };
  }
  if (input.register && input.password.length < PASSWORD_MIN) {
    return {
      code: 'password-short',
      message: `Your password needs at least ${PASSWORD_MIN} characters.`,
    };
  }
  if (input.register && input.password !== input.repeatPassword) {
    return { code: 'password-mismatch', message: 'Those two passwords do not match.' };
  }
  if (input.register && !input.agreedToTerms) {
    return {
      code: 'terms-required',
      message: 'You need to agree to the terms of service to register.',
    };
  }

  if (!input.captchaSolved) {
    return { code: 'captcha-pending', message: 'Finishing the security check…' };
  }

  return null;
}

/** The button is live exactly when nothing is blocking it and nothing is in flight. */
export function canSubmitJoin(input: JoinGateInput): boolean {
  return !input.busy && joinBlockReason(input) === null;
}

/**
 * Should the reason be on screen yet?
 *
 * An untouched form must not open with a complaint — that reads as an error the
 * player has already made. So a field problem waits until they have typed
 * something, while `insecure-context` shows immediately: it is not their doing,
 * it will not clear, and it is the whole explanation for a button that never
 * comes alive.
 */
export function shouldShowBlock(block: JoinBlock | null, input: JoinGateInput): boolean {
  if (!block) return false;
  if (block.code === 'insecure-context') return true;
  // Mirrors `ResetPasswordScreen`'s own `mismatch` computed: a complaint about
  // the *repeat* field must wait for that field, not for the first one.
  if (block.code === 'password-mismatch') return input.repeatPassword.length > 0;
  return (
    input.nickname.length > 0 || input.email.length > 0 || input.password.length > 0
  );
}

/** Whether this browser can run the proof-of-work at all. */
export function captchaCapable(scope: { isSecureContext?: boolean; crypto?: Crypto } = globalThis): boolean {
  return (
    scope.isSecureContext === true &&
    typeof scope.crypto?.subtle?.digest === 'function'
  );
}
